// ─────────────────────────────────────────────────────────────────────────
// Frontend deploy-freshness guard.
//
// PROBLEM this solves: index.html is served with `max-age=0, must-revalidate`,
// so a *full page load* always fetches the newest hashed bundle. But this is a
// single-page app — once it boots, client-side navigation NEVER re-requests
// index.html, so a long-lived session (very common on mobile where the app is
// added to the home screen or just kept open/backgrounded for days) keeps
// running the OLD JavaScript in memory even after we ship a new deploy. That is
// exactly the recurring "I fixed & deployed it but the user still sees the old
// behaviour" trap.
//
// FIX: periodically compare the bundle that is actually running (the
// <script src="/static/js/main.<hash>.js"> that booted us) against the bundle
// that is currently deployed (read from /asset-manifest.json, which Vercel
// serves fresh). When they differ a new deploy exists, so we reload the SPA at
// a non-disruptive moment (when it is visible and the user is not mid-typing;
// otherwise on the next visibility/focus).
//
// LOOP SAFETY: a CDN can transiently serve a stale index.html (old main.js)
// while /asset-manifest.json is already fresh (new hash). A naive reloader
// would then reload forever. So we remember (in sessionStorage) the hash we
// last reloaded FOR and never reload again for that same target until the app
// has actually booted on a matching bundle. The guard is cleared only once
// `loaded === deployed` (versions reconciled), NOT on every boot.
// ─────────────────────────────────────────────────────────────────────────

const MANIFEST_URL = "/asset-manifest.json";
const POLL_MS = 60 * 1000; // check every 60s
const RELOAD_GUARD_KEY = "vc_reloaded_for"; // hash we last reloaded for (persists across reloads)

let loadedMainJs = null; // e.g. "main.b998b1b5.js" — the running bundle
let started = false;

function fileName(path) {
  return path ? String(path).split("/").pop() : null;
}

// The main.<hash>.js that actually booted THIS running app.
function currentLoadedMainJs() {
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  const src = scripts
    .map((s) => s.getAttribute("src") || "")
    .find((s) => /\/static\/js\/main\.[a-z0-9]+\.js/i.test(s));
  return fileName(src);
}

// The main.<hash>.js that is currently DEPLOYED (fresh, uncached).
async function fetchDeployedMainJs() {
  try {
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return null;
    const json = await res.json();
    const main = (json && json.files && json.files["main.js"]) || (json && json["main.js"]);
    return fileName(main);
  } catch (_e) {
    return null; // offline / transient — try again next tick
  }
}

function getGuard() {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch (_e) {
    return null;
  }
}

function setGuard(hash) {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, hash);
  } catch (_e) {
    /* private mode — best effort */
  }
}

function clearGuard() {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch (_e) {
    /* ignore */
  }
}

function isTyping() {
  const el = document.activeElement;
  return !!(
    el &&
    (el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA" ||
      el.tagName === "SELECT" ||
      el.isContentEditable)
  );
}

async function check() {
  if (!loadedMainJs) return;
  const deployed = await fetchDeployedMainJs();
  if (!deployed) return;

  if (deployed === loadedMainJs) {
    // Running the current build → nothing to do. If we had reloaded to get
    // here, the versions are now reconciled, so drop the guard.
    if (getGuard()) clearGuard();
    return;
  }

  // A newer bundle is live. If we already reloaded for exactly this hash and
  // still didn't land on it (a lagging edge cache is still serving the old
  // index.html), do NOT reload again — that would loop. Wait for the edge to
  // catch up; the next check that sees a match will clear the guard.
  if (getGuard() === deployed) return;

  // Reload only when it won't interrupt the user.
  if (document.visibilityState === "visible" && !isTyping()) {
    setGuard(deployed);
    window.location.reload();
  }
  // Otherwise a later focus/visibility check will reload once we're visible.
}

export function initVersionCheck() {
  if (typeof window === "undefined" || started) return;
  loadedMainJs = currentLoadedMainJs();
  // No hashed bundle → dev server (webpack) — nothing to guard.
  if (!loadedMainJs) return;
  started = true;

  setInterval(check, POLL_MS);
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });

  // First check shortly after boot (don't compete with initial data fetches).
  setTimeout(check, 15 * 1000);
}
